import type { JSX } from 'react';
import { Card, IconButton } from '@ksyos/design-system';
import { useEffect, useMemo, useState } from 'react';
import type { ServiceOrganizationSettingRoleDistribution } from '@common/interfaces/api/serviceOrganizationRoleDistributions';
import type { UpdateRoleDistributionsRequestBody } from '@common/interfaces/api/serviceOrganizationSettings';
import type { ServiceOrganizationRole } from '@common/interfaces/api/servicesApi';
import type { IServiceOrganizationSettingRoleDistribution } from '@frontend/components/interfaces/serviceOrganizationSettingRoleDistribution';
import { ManagerAuthorization, ServiceOrganizationRole as Role } from '@common/enums';
import {
    convertPercentageToWeight,
    convertWeightToPercentage,
    getGreatestCommonDivisor,
    getTotalWeightOfRole,
} from '@common/utils/distributionUtils';
import {
    getRoleDistributions,
    updateRoleDistributions,
} from '@frontend/services/api/serviceOrganizationSettingApiService';
import { getOrganizationRoles } from '@frontend/services/api/servicesApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils/hasAuthorization';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useServiceOrganizationSettingContext } from '@frontend/utils/hooks/useServiceOrganizationsSettingsContext';
import type {
    roleDistributionErrorTypes,
    ServiceOrganizationRoleDistributionsErrors,
} from './serviceOrganizationRoleDistributionTypes';
import { ServiceOrganizationSettingFormMode } from '../../types';
import { ServiceOrganizationsRoleDistributionsEditAndSettingButtons } from './ServiceOrganizationRoleDistributionsEditAndSettingButtons';
import { getServiceOrganizationRolesSelectableForDistributions } from './serviceOrganizationRoleDistributionSettings';
import { ServiceOrganizationRoleDistributionsRead } from './ServiceOrganizationRoleDistributionsRead';

const emptyErrorsByRole: Record<Role, ServiceOrganizationRoleDistributionsErrors> = {
    [Role.hospital]: new Map(),
    [Role.psychology]: new Map(),
    [Role.dietetics]: new Map(),
    [Role.lifestyleCoaching]: new Map(),
    [Role.policlinic]: new Map(),
};

export function ServiceOrganizationRoleDistributions(): JSX.Element {
    const abortSignal = useAbortSignal();
    const { serviceOrganizationSetting } = useServiceOrganizationSettingContext();
    const canEdit = hasOneAuthorization(ManagerAuthorization.organizationAddEdit);
    const [errorsByRole, setErrorsByRole] =
        useState<Record<Role, ServiceOrganizationRoleDistributionsErrors>>(emptyErrorsByRole);
    const [initialServiceOrganizationRoleDistributions, setInitialServiceOrganizationRoleDistributions] = useState<
        IServiceOrganizationSettingRoleDistribution[]
    >([]);
    const [isEquallyDistributedByRole, setIsEquallyDistributedByRole] = useState<Record<Role, boolean>>({
        [Role.hospital]: false,
        [Role.psychology]: false,
        [Role.dietetics]: false,
        [Role.lifestyleCoaching]: false,
        [Role.policlinic]: false,
    });
    const [isSaving, setIsSaving] = useState(false);
    const [serviceOrganizationRoles, setServiceOrganizationRoles] = useState<ServiceOrganizationRole[]>([]);
    const [mode, setMode] = useState<ServiceOrganizationSettingFormMode>(ServiceOrganizationSettingFormMode.read);

    const roles = useMemo(
        () => getServiceOrganizationRolesSelectableForDistributions(serviceOrganizationSetting.service.code),
        [serviceOrganizationSetting.service.code]
    );

    useEffect(() => {
        loadServiceOrganizationRoles();
    }, []);

    useEffect(() => {
        if (mode === ServiceOrganizationSettingFormMode.read) {
            loadServiceOrganizationRoleDistributions();
            setErrorsByRole(emptyErrorsByRole);
        }
    }, [mode]);

    return (
        <Card>
            <Card.Transition type={mode} />
            <Card.Header
                action={
                    mode === ServiceOrganizationSettingFormMode.read && canEdit ? (
                        <IconButton
                            icon="edit-light"
                            labelText="edit"
                            onClick={() => setMode(ServiceOrganizationSettingFormMode.edit)}
                        />
                    ) : null
                }
                title={getSelectedLanguage().serviceOrganizationSettingRoleDistributions.distributionOrganizations}
            />
            <Card.Body>
                {mode === ServiceOrganizationSettingFormMode.read ? (
                    <ServiceOrganizationRoleDistributionsRead
                        roles={roles}
                        serviceOrganizationRoleDistributions={initialServiceOrganizationRoleDistributions}
                    />
                ) : (
                    <ServiceOrganizationsRoleDistributionsEditAndSettingButtons
                        errorsByRole={errorsByRole}
                        handleClose={async () => setMode(ServiceOrganizationSettingFormMode.read)}
                        handleSubmit={handleSubmit}
                        isEquallyDistributedByRole={isEquallyDistributedByRole}
                        isSaving={isSaving}
                        roles={roles}
                        serviceOrganizationRoleDistributions={initialServiceOrganizationRoleDistributions}
                        serviceOrganizationRoles={serviceOrganizationRoles}
                        setErrorsByRole={setErrorsByRole}
                        setIsEquallyDistributed={(role, value) =>
                            setIsEquallyDistributedByRole({ ...isEquallyDistributedByRole, [role]: value })
                        }
                        validateRoleDistributionsForRole={validateRoleDistributionsForRole}
                    />
                )}
            </Card.Body>
        </Card>
    );

    async function loadServiceOrganizationRoles() {
        const { items } = await getOrganizationRoles(serviceOrganizationSetting.service.id, undefined, abortSignal);
        setServiceOrganizationRoles(items);
    }

    async function loadServiceOrganizationRoleDistributions(): Promise<void> {
        const { items } = await getRoleDistributions(serviceOrganizationSetting.id, undefined, abortSignal);
        setInitialIsEquallyDistributedByRole(items);

        const roleDistributionsWithPercentage: IServiceOrganizationSettingRoleDistribution[] = items.map(
            (distributionRole) => ({
                ...distributionRole,
                percentage: convertWeightToPercentage(
                    distributionRole.weight,
                    getTotalWeightOfRole(distributionRole.role, items)
                ),
            })
        );

        setInitialServiceOrganizationRoleDistributions(roleDistributionsWithPercentage);
    }

    function setInitialIsEquallyDistributedByRole(roleDistributions: ServiceOrganizationSettingRoleDistribution[]) {
        const isEquallyDistributedByRoleCopy = { ...isEquallyDistributedByRole };
        for (const role of roles) {
            const roleDistributionsForRole = roleDistributions.filter(
                (roleDistribution) => roleDistribution.role === role
            );
            isEquallyDistributedByRoleCopy[role] = roleDistributionsForRole.every(
                (roleDistribution) => roleDistribution.weight === 1
            );
        }
        setIsEquallyDistributedByRole(isEquallyDistributedByRoleCopy);
    }

    async function handleSubmit(
        serviceOrganizationRoleDistributionsToUpdate: IServiceOrganizationSettingRoleDistribution[]
    ): Promise<void> {
        setIsSaving(true);

        let hasErrors = false;
        const errors = { ...emptyErrorsByRole };

        const updateRoleDistributionsRequestBody: UpdateRoleDistributionsRequestBody = [];
        for (const role of roles) {
            const roleDistributionsForRole = filterRoleDistributionsByRole(
                serviceOrganizationRoleDistributionsToUpdate,
                role
            );
            if (roleDistributionsForRole.length === 0) {
                continue;
            }

            let greatestCommonDivisor: number;

            if (!isEquallyDistributedByRole[role]) {
                const errorsForRole = getErrorsForRoleDistributions(roleDistributionsForRole);
                if (errorsForRole) {
                    hasErrors = true;
                    errors[role] = errorsForRole;
                }

                greatestCommonDivisor = roleDistributionsForRole
                    .map(({ percentage }) => percentage)
                    .reduce(getGreatestCommonDivisor);
            }

            const updateRoleDistributionsRequestBodyForRole = roleDistributionsForRole.map((roleDistribution) => ({
                role: roleDistribution.role,
                serviceOrganizationRoleId: roleDistribution.serviceOrganizationRole.id,
                serviceOrganizationSettingId: roleDistribution.serviceOrganizationSetting.id,
                weight: isEquallyDistributedByRole[role]
                    ? 1
                    : convertPercentageToWeight(roleDistribution.percentage, greatestCommonDivisor),
            }));

            updateRoleDistributionsRequestBody.push(...updateRoleDistributionsRequestBodyForRole);
        }

        setErrorsByRole(errors);
        if (hasErrors) {
            setIsSaving(false);

            return;
        }

        await updateRoleDistributions(serviceOrganizationSetting.id, updateRoleDistributionsRequestBody, abortSignal);
        setMode(ServiceOrganizationSettingFormMode.read);
        setIsSaving(false);
    }

    function validateRoleDistributionsForRole(
        roleDistributions: IServiceOrganizationSettingRoleDistribution[],
        role: Role
    ): void {
        const roleDistributionsForRole = filterRoleDistributionsByRole(roleDistributions, role);
        const errors = getErrorsForRoleDistributions(roleDistributionsForRole);
        setErrorsByRole({ ...errorsByRole, [role]: errors ?? new Map() });
    }

    function getErrorsForRoleDistributions(
        roleDistributions: IServiceOrganizationSettingRoleDistribution[]
    ): ServiceOrganizationRoleDistributionsErrors | undefined {
        let errors: Map<(typeof roleDistributionErrorTypes)[number], string> = new Map();

        const totalPercentage = roleDistributions.reduce((total, { percentage }) => total + percentage, 0);
        if (totalPercentage !== 100) {
            errors = errors.set(
                'totalPercentageNot100',
                getSelectedLanguage().serviceOrganizationSettingRoleDistributions.totalPercentageNot100
            );
        }

        if (roleDistributions.some(({ percentage }) => percentage < 1)) {
            errors = errors.set(
                'percentageHigherThan0',
                getSelectedLanguage().serviceOrganizationSettingRoleDistributions.percentageHigherThan0
            );
        }

        if (roleDistributions.some(({ percentage }) => typeof percentage === 'undefined' || percentage === null)) {
            errors = errors.set(
                'percentageRequired',
                getSelectedLanguage().serviceOrganizationSettingRoleDistributions.percentageRequired
            );
        }

        return errors.size > 0 ? errors : undefined;
    }

    function filterRoleDistributionsByRole(
        roleDistributions: IServiceOrganizationSettingRoleDistribution[],
        role: Role
    ): IServiceOrganizationSettingRoleDistribution[] {
        return roleDistributions.filter((roleDistribution) => roleDistribution.role === role);
    }
}
